import { useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Trophy, Clock, Sparkles } from "lucide-react";

const ICONS = {
  success: { Icon: CheckCircle2, ring: "bg-emerald-50", tint: "text-emerald-600" },
  trophy:  { Icon: Trophy,       ring: "bg-amber-50",   tint: "text-amber-600" },
  clock:   { Icon: Clock,        ring: "bg-amber-50",  tint: "text-amber-700" },
  sparkle: { Icon: Sparkles,     ring: "bg-amber-50",  tint: "text-amber-700" },
};

/**
 * A soft, centered celebration modal for critical positive actions.
 * Auto-dismisses after `autoCloseMs` (0 to disable).
 */
export default function SuccessModal({
  open,
  onOpenChange,
  variant = "success",
  title,
  body,
  ctaLabel = "Continue",
  onCta,
  autoCloseMs = 4000,
  testid = "success-modal",
}) {
  const { Icon, ring, tint } = ICONS[variant] || ICONS.success;

  useEffect(() => {
    if (!open || !autoCloseMs) return;
    const t = setTimeout(() => onOpenChange(false), autoCloseMs);
    return () => clearTimeout(t);
  }, [open, autoCloseMs, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testid}
        className="max-w-sm border-0 p-0 shadow-2xl backdrop-blur-sm"
      >
        <div className="flex flex-col items-center gap-4 rounded-lg bg-white px-6 pt-8 pb-6 text-center">
          <div className={`flex h-16 w-16 items-center justify-center rounded-full ${ring}`}>
            <Icon className={`h-8 w-8 ${tint}`} strokeWidth={2.25} />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-lg font-semibold tracking-tight text-neutral-900">{title}</h3>
            {body && <p className="text-sm leading-relaxed text-neutral-600">{body}</p>}
          </div>
          {ctaLabel && (
            <Button
              onClick={() => { onOpenChange(false); onCta?.(); }}
              className="mt-2 w-full rounded-lg bg-amber-700 hover:bg-amber-800"
              data-testid={`${testid}-cta`}
            >
              {ctaLabel}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
